import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Plus, Pencil, Trash2, Layers, Coins, Loader2, AlertTriangle, QrCode, Copy, KeyRound, Download, Moon } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

import api, { formatApiError } from "@/lib/api";
import { listSucursales } from "@/services/sucursalesService";
import { TIMEZONES } from "@/lib/timezones";
import DashboardLayout from "@/components/DashboardLayout";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { QRCodeCanvas } from "qrcode.react";
import { Input } from "@/components/ui/input";
import { CurrencyField } from "@/components/ui/forms";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const MODULES = [
  { key: "modulo_estacionamiento", label: "Estacionamiento" },
  { key: "modulo_valet", label: "Valet" },
  { key: "cobro_tarjeta_mp", label: "Pago Tarjeta" },
  { key: "cobro_recargas_taecel", label: "Recargas" },
  { key: "cobro_servicios_prontipagos", label: "Servicios" },
];

const emptyForm = {
  nombre: "",
  auto_recarga_activa: false,
  umbral_folios_alerta: 0,
  id_paquete_auto_recarga: null,
  modulo_estacionamiento: false,
  modulo_valet: false,
  cobro_tarjeta_mp: false,
  cobro_recargas_taecel: false,
  cobro_servicios_prontipagos: false,
  // Sprint 8.0 — Valet Parking (candado financiero)
  valet_activo: false,
  tarifa_valet: "",
  // Factor de Riesgo Valet
  habilitar_factor_riesgo: false,
  cuota_factor_riesgo: "",
  voz_ia_activa: false,
  foto_ia_activa: false,
  // Sprint 9.0 — prefijo de folio
  prefijo_folio: "",
  // Sprint 3 — datos operativos
  calle: "",
  numero: "",
  codigo_postal: "",
  colonia: "",
  municipio: "",
  estado_direccion: "",
  capacidad: "",
  horario: "",
  telefono: "",
  responsable: "",
  logo_base64: "",
  contrato_adhesion: "",
  // Sprint 15.3 — Boleto perdido / leyenda legal impresa
  costo_boleto_perdido: "100.00",
  leyenda_ticket_corta: "",
  // Sprint 16.0 — modelo de cobro
  modelo_cobro: "COMBINADO",
  // Zona horaria IANA de la sucursal
  zona_horaria: "America/Mexico_City",
  // Sprint 31.0 — habilitar cortesías por sucursal
  cortesias_activas: true,
  // Sprint 17.0 — solicitud de propinas (solo Tarjeta/Point)
  propinas_activas: false,
  // Sprint 17.2 — tope máximo de propina (% del subtotal)
  tope_propina_pct: 100,
  // Punto 2 — horarios estructurados
  horario_atencion: "",
  horarios_json: "",
  horarios: null,
  // Sprint 18.0 — permisos financieros por sucursal
  requiere_permiso_corte: false,
  requiere_permiso_gastos: false,
  // Sprint Pernoctas — pensión nocturna (tarifa fija de pernocta para Valet)
  tarifa_pernocta_activa: false,
  monto_pernocta: "150.00",
  minutos_tolerancia_pos_cierre: 120,
  minutos_tolerancia_valet: 0,
};

function Pill({ on, children }) {
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] tracking-wider uppercase font-bold border ${on ? "bg-[#10B981]/10 text-[#0f7a5b] border-[#10B981]/40" : "bg-gray-100 text-gray-400 border-gray-200"}`}>
      {children}
    </span>
  );
}

// Punto 2 — Horarios estructurados por sucursal
const makeDefaultHorarios = () => ({
  is247: false,
  lv: { activo: true, apertura: "07:00", cierre: "22:00" },
  sab: { activo: true, apertura: "08:00", cierre: "20:00" },
  dom: { activo: false, apertura: "09:00", cierre: "14:00" },
});

const DIA_LABEL = { lv: "L-V", sab: "SÁB", dom: "DOM" };

// Construye la cadena compacta para el ticket. Ej: "L-V 07:00-22:00 | SÁB 08:00-20:00 | DOM CERRADO" o "Abierto 24/7".
function formatHorario(h) {
  if (!h) return "";
  if (h.is247) return "Abierto 24/7";
  return ["lv", "sab", "dom"].map((k) => {
    const d = h[k] || {};
    return d.activo && d.apertura && d.cierre ? `${DIA_LABEL[k]} ${d.apertura}-${d.cierre}` : `${DIA_LABEL[k]} CERRADO`;
  }).join(" | ");
}

function parseHorarios(json) {
  const def = makeDefaultHorarios();
  if (!json) return def;
  try {
    const p = typeof json === "string" ? JSON.parse(json) : json;
    return {
      is247: !!p.is247,
      lv: { ...def.lv, ...(p.lv || {}) },
      sab: { ...def.sab, ...(p.sab || {}) },
      dom: { ...def.dom, ...(p.dom || {}) },
    };
  } catch { return def; }
}

function HorarioDia({ label, testid, dia, onChange }) {
  return (
    <div className="flex items-center gap-3 flex-wrap py-1" data-testid={`horario-${testid}`}>
      <label className="flex items-center gap-2 min-w-[140px] cursor-pointer">
        <input type="checkbox" checked={!!dia.activo} onChange={(e) => onChange({ activo: e.target.checked })}
          data-testid={`horario-${testid}-activo`} className="h-4 w-4 accent-[#FFCC00]" />
        <span className="text-xs font-bold uppercase tracking-wider">{label}</span>
      </label>
      {dia.activo ? (
        <div className="flex items-center gap-2">
          <Input type="time" value={dia.apertura || ""} onChange={(e) => onChange({ apertura: e.target.value })}
            data-testid={`horario-${testid}-apertura`} className="h-9 w-32 rounded-none border-[#0A0A0A]" />
          <span className="text-xs text-[#71717A]">a</span>
          <Input type="time" value={dia.cierre || ""} onChange={(e) => onChange({ cierre: e.target.value })}
            data-testid={`horario-${testid}-cierre`} className="h-9 w-32 rounded-none border-[#0A0A0A]" />
        </div>
      ) : (
        <span className="text-xs font-bold text-[#EF4444] tracking-wider">CERRADO</span>
      )}
    </div>
  );
}

export default function Sucursales() {
  const [items, setItems] = useState([]);
  const [paquetes, setPaquetes] = useState([]);
  const [empresa, setEmpresa] = useState(null);
  const [qrTarget, setQrTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null=create, obj=edit
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [prefijoError, setPrefijoError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([listSucursales(), api.get("/paquetes")]);
      setItems(s.data);
      setPaquetes(p.data);
      try { const e = await api.get("/mi-empresa"); setEmpresa(e.data); } catch { setEmpresa(null); }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, horarios: makeDefaultHorarios() });
    setPrefijoError("");
    setSheetOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      nombre: s.nombre,
      auto_recarga_activa: s.auto_recarga_activa,
      umbral_folios_alerta: s.umbral_folios_alerta,
      id_paquete_auto_recarga: s.id_paquete_auto_recarga,
      modulo_estacionamiento: s.modulo_estacionamiento,
      modulo_valet: s.modulo_valet,
      cobro_tarjeta_mp: s.cobro_tarjeta_mp,
      cobro_recargas_taecel: s.cobro_recargas_taecel,
      cobro_servicios_prontipagos: s.cobro_servicios_prontipagos,
      valet_activo: s.valet_activo || false,
      tarifa_valet: s.tarifa_valet ?? "",
      habilitar_factor_riesgo: s.habilitar_factor_riesgo || false,
      cuota_factor_riesgo: s.cuota_factor_riesgo ?? "",
      voz_ia_activa: s.voz_ia_activa || false,
      foto_ia_activa: s.foto_ia_activa || false,
      prefijo_folio: s.prefijo_folio || "",
      calle: s.calle || "",
      numero: s.numero || "",
      codigo_postal: s.codigo_postal || "",
      colonia: s.colonia || "",
      municipio: s.municipio || "",
      estado_direccion: s.estado_direccion || "",
      capacidad: s.capacidad ?? "",
      horario: s.horario || "",
      telefono: s.telefono || "",
      responsable: s.responsable || "",
      logo_base64: s.logo_base64 || "",
      contrato_adhesion: s.contrato_adhesion || "",
      costo_boleto_perdido: s.costo_boleto_perdido ?? "100.00",
      leyenda_ticket_corta: s.leyenda_ticket_corta || "",
      modelo_cobro: s.modelo_cobro || "COMBINADO",
      zona_horaria: s.zona_horaria || "America/Mexico_City",
      cortesias_activas: s.cortesias_activas !== false,
      propinas_activas: s.propinas_activas || false,
      tope_propina_pct: Math.round(Number(s.tope_propina_pct ?? 100)) || 100,
      horario_atencion: s.horario_atencion || s.horario || "",
      horarios_json: s.horarios_json || "",
      horarios: parseHorarios(s.horarios_json),
      requiere_permiso_corte: !!s.requiere_permiso_corte,
      requiere_permiso_gastos: !!s.requiere_permiso_gastos,
      tarifa_pernocta_activa: !!s.tarifa_pernocta_activa,
      monto_pernocta: s.monto_pernocta ?? "150.00",
      minutos_tolerancia_pos_cierre: Number(s.minutos_tolerancia_pos_cierre ?? 120),
      minutos_tolerancia_valet: Number(s.minutos_tolerancia_valet ?? 0),
    });
    setPrefijoError("");
    setSheetOpen(true);
  };

  // Sprint 15.0 — Procesa el logo a 128px B/N (1-bit) y lo guarda base64 en el form
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const maxW = 200;
        let w = img.width, h = img.height;
        if (w > maxW) { h = Math.max(1, Math.round(h * maxW / w)); w = maxW; }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        const data = ctx.getImageData(0, 0, w, h);
        const d = data.data;
        for (let i = 0; i < d.length; i += 4) {
          const a = d[i + 3];
          const lum = a < 128 ? 255 : (0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]);
          const v = lum < 128 ? 0 : 255;
          d[i] = d[i + 1] = d[i + 2] = v; d[i + 3] = 255;
        }
        ctx.putImageData(data, 0, 0);
        setForm((f) => ({ ...f, logo_base64: canvas.toDataURL("image/png") }));
        toast.success("Logo procesado (200px, B/N)");
      };
      img.onerror = () => toast.error("No se pudo leer la imagen");
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const save = async () => {
    if (!form.nombre.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    setPrefijoError("");
    try {
      const payload = {
        ...form,
        umbral_folios_alerta: Number(form.umbral_folios_alerta) || 0,
        capacidad: form.capacidad === "" || form.capacidad == null ? null : Number(form.capacidad),
        valet_activo: form.valet_activo,
        tarifa_valet: form.valet_activo ? (Number(form.tarifa_valet) || 0).toFixed(2) : "0.00",
        habilitar_factor_riesgo: form.valet_activo ? !!form.habilitar_factor_riesgo : false,
        cuota_factor_riesgo: (form.valet_activo && form.habilitar_factor_riesgo) ? (Number(form.cuota_factor_riesgo) || 0).toFixed(2) : "0.00",
        costo_boleto_perdido: (Number(form.costo_boleto_perdido) || 0).toFixed(2),
        leyenda_ticket_corta: (form.leyenda_ticket_corta || "").slice(0, 300),
        modelo_cobro: form.modelo_cobro || "COMBINADO",
        zona_horaria: form.zona_horaria || "America/Mexico_City",
        cortesias_activas: !!form.cortesias_activas,
        voz_ia_activa: !!form.voz_ia_activa,
        foto_ia_activa: !!form.foto_ia_activa,
        tope_propina_pct: (Number(form.tope_propina_pct) || 100).toFixed(2),
        horario_atencion: formatHorario(form.horarios),
        horarios_json: JSON.stringify(form.horarios || makeDefaultHorarios()),
        tarifa_pernocta_activa: !!form.tarifa_pernocta_activa,
        monto_pernocta: (Number(form.monto_pernocta) || 0).toFixed(2),
        minutos_tolerancia_pos_cierre: Number(form.minutos_tolerancia_pos_cierre) || 0,
        minutos_tolerancia_valet: Number(form.minutos_tolerancia_valet) || 0,
      };
      delete payload.horarios;
      if (form.prefijo_folio && form.prefijo_folio.trim()) {
        payload.prefijo_folio = form.prefijo_folio.trim().toUpperCase();
      } else {
        delete payload.prefijo_folio;
      }
      if (editing) {
        await api.put(`/sucursales/${editing.id_sucursal}`, payload);
        toast.success("Sucursal actualizada");
      } else {
        await api.post("/sucursales", payload);
        toast.success("Sucursal creada (saldo inicial: 0 folios)");
      }
      setSheetOpen(false);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = formatApiError(detail);
      if (err.response?.status === 400 && typeof msg === "string" && msg.toLowerCase().includes("prefijo")) {
        setPrefijoError("Este prefijo ya está en uso por otra sucursal. Ingresa uno distinto.");
      } else {
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/sucursales/${deleteTarget.id_sucursal}`);
      toast.success("Sucursal desactivada");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Bolsa Global de Folios del Tenant (empresa). Ya no se usan saldos individuales por sucursal.
  const saldoGlobal = Number(empresa?.saldo_folios ?? 0);
  const umbralGlobal = Number(empresa?.umbral_folios_alerta ?? 0);
  const globalEnAlerta = umbralGlobal > 0 && saldoGlobal <= umbralGlobal;
  const qrUrl = qrTarget && empresa
    ? `${window.location.origin}/?codigo=${empresa.codigo_operativo}&sucursal=${qrTarget.id_sucursal}`
    : "";

  return (
    <DashboardLayout>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        {(() => {
          if (!globalEnAlerta) return null;
          return (
            <div data-testid="folios-bajos-banner" className="mb-6 border border-[#F59E0B] bg-[#F59E0B]/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-[#B45309] shrink-0" />
              <div className="flex-1">
                <p className="font-display font-bold text-sm">Saldo de folios bajo</p>
                <p className="text-xs text-[#52525B]">Tu Bolsa Global tiene {saldoGlobal.toLocaleString()} folios, por debajo del umbral de alerta ({umbralGlobal.toLocaleString()}). Recarga para no interrumpir la operación.</p>
              </div>
              <Link to="/app/recargas" data-testid="banner-comprar-folios" className="btn-hard h-10 px-4 bg-[#0A0A0A] text-white border border-[#0A0A0A] font-bold uppercase tracking-wider text-xs inline-flex items-center gap-2 shrink-0"><Coins className="h-4 w-4" /> Comprar folios</Link>
            </div>
          );
        })()}
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2">Gestión de patios operativos</p>
            <h1 className="font-display text-4xl font-black tracking-tight">Sucursales</h1>
          </div>
          <button onClick={openCreate} data-testid="create-sucursal-btn"
            className="btn-hard h-11 px-5 bg-[#FFCC00] text-[#0A0A0A] border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm flex items-center gap-2">
            <Plus className="h-4 w-4" /> Nueva sucursal
          </button>
        </div>

        {/* Código operativo banner */}
        {empresa && (
          <div className="mb-8 bg-[#0A0A0A] text-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6" data-testid="codigo-operativo-banner">
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-white/50 mb-2 flex items-center gap-2"><KeyRound className="h-3.5 w-3.5 text-[#FFCC00]" /> Código operativo de tu empresa</p>
              <p className="text-white/60 text-xs max-w-md">Compártelo con tu personal para el acceso operativo, o genera un QR por sucursal.</p>
            </div>
            <div className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#FFCC00]" data-testid="codigo-operativo-value">
              {empresa.codigo_operativo}
            </div>
          </div>
        )}

        {/* Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7] mb-8">
          <div className="bg-white p-6" data-testid="metric-total">
            <div className="flex items-center gap-2 text-[#52525B] mb-3"><Layers className="h-4 w-4" /><span className="text-xs tracking-[0.15em] uppercase font-bold">Sucursales activas</span></div>
            <p className="font-display text-4xl font-black">{items.length}</p>
          </div>
          <div className="bg-white p-6" data-testid="metric-folios">
            <div className="flex items-center gap-2 text-[#52525B] mb-3"><Coins className="h-4 w-4" /><span className="text-xs tracking-[0.15em] uppercase font-bold">Folios totales</span></div>
            <p className="font-display text-4xl font-black">{saldoGlobal.toLocaleString()}</p>
          </div>
          <div className="bg-white p-6 col-span-2 lg:col-span-1" data-testid="metric-alertas">
            <div className="flex items-center gap-2 text-[#52525B] mb-3"><AlertTriangle className="h-4 w-4" /><span className="text-xs tracking-[0.15em] uppercase font-bold">En alerta</span></div>
            <p className="font-display text-4xl font-black text-[#F59E0B]">{globalEnAlerta ? items.length : 0}</p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#e4e4e7]">
          {loading ? (
            <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#52525B]" /></div>
          ) : items.length === 0 ? (
            <div className="p-16 text-center text-[#52525B]" data-testid="empty-state">
              <Layers className="h-8 w-8 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No hay sucursales. Crea la primera.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sucursales-table">
                <thead>
                  <tr className="border-b border-[#e4e4e7] text-left">
                    <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Nombre</th>
                    <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Prefijo</th>
                    <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Saldo folios</th>
                    <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Módulos</th>
                    <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((s) => {
                    return (
                      <tr key={s.id_sucursal} className="border-b border-[#f0f0f1] hover:bg-gray-50 transition-colors duration-200" data-testid={`sucursal-row-${s.id_sucursal}`}>
                        <td className="px-5 py-4 font-semibold">{s.nombre}</td>
                        <td className="px-5 py-4">
                          <span className="inline-block font-mono font-bold tracking-[0.15em] px-2 py-0.5 bg-[#0A0A0A] text-[#FFCC00] text-xs" data-testid={`prefijo-${s.id_sucursal}`}>{s.prefijo_folio || "—"}</span>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`font-display font-bold ${globalEnAlerta ? "text-[#F59E0B]" : ""}`} data-testid={`saldo-folios-${s.id_sucursal}`}>{saldoGlobal.toLocaleString()}</span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-wrap gap-1.5">
                            {MODULES.filter((m) => s[m.key]).map((m) => <Pill key={m.key} on>{m.label}</Pill>)}
                            {MODULES.every((m) => !s[m.key]) && <span className="text-xs text-gray-400">—</span>}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex justify-end gap-2">
                            {empresa && (
                              <button onClick={() => setQrTarget(s)} data-testid={`qr-${s.id_sucursal}`}
                                className="h-8 px-2.5 flex items-center gap-1 border border-[#e4e4e7] hover:border-[#0A0A0A] transition-colors duration-200 text-[11px] font-bold uppercase tracking-wide"><QrCode className="h-3.5 w-3.5" /> QR</button>
                            )}
                            <button onClick={() => openEdit(s)} data-testid={`edit-${s.id_sucursal}`}
                              className="h-8 w-8 flex items-center justify-center border border-[#e4e4e7] hover:border-[#0A0A0A] transition-colors duration-200"><Pencil className="h-3.5 w-3.5" /></button>
                            <button onClick={() => setDeleteTarget(s)} data-testid={`delete-${s.id_sucursal}`}
                              className="h-8 w-8 flex items-center justify-center border border-[#e4e4e7] hover:border-[#EF4444] hover:text-[#EF4444] transition-colors duration-200"><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </motion.div>

      {/* Sheet form */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-display text-2xl font-black tracking-tight">
              {editing ? "Editar sucursal" : "Nueva sucursal"}
            </SheetTitle>
            <SheetDescription>Configura el nombre, módulos y auto-recarga de la sucursal.</SheetDescription>
          </SheetHeader>

          <div className="space-y-6 mt-6">
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Nombre</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Patio Centro" data-testid="form-nombre"
                className="h-11 rounded-none border-[#0A0A0A] focus-visible:ring-2 focus-visible:ring-[#FFCC00]" />
            </div>

            {/* Sprint 9.0 — Código Operativo / Prefijo de Folio */}
            <div className="space-y-1.5">
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Código Operativo / Prefijo de Folio</Label>
              <Input value={form.prefijo_folio} maxLength={3}
                onChange={(e) => { setForm({ ...form, prefijo_folio: e.target.value.toUpperCase() }); if (prefijoError) setPrefijoError(""); }}
                placeholder="PTC" data-testid="form-prefijo-folio"
                className={`h-11 rounded-none uppercase tracking-[0.3em] font-bold focus-visible:ring-2 focus-visible:ring-[#FFCC00] ${prefijoError ? "border-[#EF4444]" : "border-[#0A0A0A]"}`} />
              {prefijoError ? (
                <p className="text-[11px] font-semibold text-[#EF4444]" data-testid="form-prefijo-error">{prefijoError}</p>
              ) : (
                <p className="text-[11px] text-[#71717A]">3 letras únicas para los folios de la sucursal (Ej. PTC)</p>
              )}
            </div>

            {/* Datos operativos (Sprint 3) */}
            <div className="border border-[#e4e4e7] p-4 space-y-4">
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B] block">Datos operativos</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2 sm:col-span-1"><Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Calle</Label>
                  <Input value={form.calle} onChange={(e) => setForm({ ...form, calle: e.target.value })} data-testid="form-calle" className="h-10 rounded-none border-[#0A0A0A]" /></div>
                <div className="space-y-1.5 col-span-2 sm:col-span-1"><Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Número</Label>
                  <Input value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} data-testid="form-numero" className="h-10 rounded-none border-[#0A0A0A]" /></div>
                <div className="space-y-1.5"><Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Código postal</Label>
                  <Input value={form.codigo_postal} onChange={(e) => setForm({ ...form, codigo_postal: e.target.value })} data-testid="form-cp" className="h-10 rounded-none border-[#0A0A0A]" /></div>
                <div className="space-y-1.5"><Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Colonia</Label>
                  <Input value={form.colonia} onChange={(e) => setForm({ ...form, colonia: e.target.value })} data-testid="form-colonia" className="h-10 rounded-none border-[#0A0A0A]" /></div>
                <div className="space-y-1.5"><Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Municipio / Alcaldía</Label>
                  <Input value={form.municipio} onChange={(e) => setForm({ ...form, municipio: e.target.value })} data-testid="form-municipio" className="h-10 rounded-none border-[#0A0A0A]" /></div>
                <div className="space-y-1.5"><Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Estado (entidad)</Label>
                  <Input value={form.estado_direccion} onChange={(e) => setForm({ ...form, estado_direccion: e.target.value })} data-testid="form-estado" className="h-10 rounded-none border-[#0A0A0A]" /></div>
                <div className="space-y-1.5"><Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Capacidad (cajones)</Label>
                  <Input type="number" value={form.capacidad} onChange={(e) => setForm({ ...form, capacidad: e.target.value })} data-testid="form-capacidad" className="h-10 rounded-none border-[#0A0A0A]" /></div>
                <div className="space-y-3 col-span-2 border-t border-[#e4e4e7] pt-3 mt-1" data-testid="horario-section">
                  <Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Horarios de atención y operación</Label>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold">Sucursal opera 24/7 (Abierto 24 Horas)</span>
                    <Switch checked={!!form.horarios?.is247}
                      onCheckedChange={(v) => setForm((f) => ({ ...f, horarios: { ...(f.horarios || makeDefaultHorarios()), is247: v } }))}
                      data-testid="horario-247-switch" className="data-[state=checked]:bg-[#10B981]" />
                  </div>
                  {!form.horarios?.is247 && (
                    <div className="space-y-1">
                      <HorarioDia label="Lunes a Viernes" testid="lv" dia={form.horarios?.lv || {}}
                        onChange={(patch) => setForm((f) => ({ ...f, horarios: { ...(f.horarios || makeDefaultHorarios()), lv: { ...((f.horarios || {}).lv || {}), ...patch } } }))} />
                      <HorarioDia label="Sábados" testid="sab" dia={form.horarios?.sab || {}}
                        onChange={(patch) => setForm((f) => ({ ...f, horarios: { ...(f.horarios || makeDefaultHorarios()), sab: { ...((f.horarios || {}).sab || {}), ...patch } } }))} />
                      <HorarioDia label="Domingos" testid="dom" dia={form.horarios?.dom || {}}
                        onChange={(patch) => setForm((f) => ({ ...f, horarios: { ...(f.horarios || makeDefaultHorarios()), dom: { ...((f.horarios || {}).dom || {}), ...patch } } }))} />
                    </div>
                  )}
                  <div className="border border-[#0A0A0A] bg-[#FAFAFA] p-3" data-testid="horario-preview-box">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-[#52525B] mb-1">Vista previa (se imprime en el ticket)</p>
                    <p className="font-mono text-sm font-bold text-[#0A0A0A]" data-testid="horario-preview">{formatHorario(form.horarios) || "—"}</p>
                  </div>
                </div>
                <div className="space-y-1.5"><Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Teléfono</Label>
                  <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid="form-telefono" className="h-10 rounded-none border-[#0A0A0A]" /></div>
                <div className="space-y-1.5"><Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Responsable</Label>
                  <Input value={form.responsable} onChange={(e) => setForm({ ...form, responsable: e.target.value })} data-testid="form-responsable" className="h-10 rounded-none border-[#0A0A0A]" /></div>
              </div>
            </div>

            {/* Sprint 15.0 — Logotipo del ticket (solo boleto del cliente) */}
            <div className="border border-[#e4e4e7] p-4 space-y-3" data-testid="logo-config-block">
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B] block">Logotipo del ticket</Label>
              <p className="text-[11px] text-[#71717A]">Se imprime en el boleto del cliente, junto al folio/QR. Se redimensiona a 128px en blanco y negro. El talón operativo no lleva logo.</p>
              <div className="flex items-center gap-4">
                {form.logo_base64 ? (
                  <img src={form.logo_base64} alt="logo" data-testid="logo-preview" className="h-16 w-auto max-w-[128px] border border-[#e4e4e7] bg-white p-1 object-contain" />
                ) : (
                  <div className="h-16 w-16 border border-dashed border-[#c4c4c4] flex items-center justify-center text-[10px] text-gray-400 text-center">Sin logo</div>
                )}
                <div className="flex flex-col gap-2">
                  <label className="btn-hard h-9 px-3 bg-[#0A0A0A] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="logo-upload-input" />
                    Subir logo
                  </label>
                  {form.logo_base64 && (
                    <button type="button" onClick={() => setForm((f) => ({ ...f, logo_base64: "" }))} data-testid="logo-remove"
                      className="h-9 px-3 border border-[#EF4444] text-[#EF4444] text-xs font-bold uppercase tracking-wider">Quitar</button>
                  )}
                </div>
              </div>
            </div>

            {/* Sprint 16.0 — Modelo de cobro por sucursal */}
            <div className="space-y-2" data-testid="modelo-cobro-block">
              <Label className="text-[11px] uppercase font-bold text-[#71717A]">Modelo de cobro</Label>
              <Select value={form.modelo_cobro} onValueChange={(v) => setForm((f) => ({ ...f, modelo_cobro: v }))}>
                <SelectTrigger data-testid="form-modelo-cobro" className="h-11 rounded-none border-[#0A0A0A]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="COMBINADO" data-testid="modelo-combinado">Combinado (estancia + valet)</SelectItem>
                  <SelectItem value="SOLO_ESTANCIA" data-testid="modelo-solo-estancia">Solo estancia (por tiempo/tarifa)</SelectItem>
                  <SelectItem value="SOLO_VALET" data-testid="modelo-solo-valet">Solo valet (tarifa fija)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#A1A1AA]">Define cómo se factura: solo tiempo de estancia, solo tarifa de valet, o la suma de ambos.</p>
            </div>

            {/* Zona horaria de la sucursal — define el "día local" para cortes, métricas, folios y horas visibles */}
            <div className="space-y-2" data-testid="zona-horaria-block">
              <Label className="text-[11px] uppercase font-bold text-[#71717A]">Zona horaria</Label>
              <Select value={form.zona_horaria} onValueChange={(v) => setForm((f) => ({ ...f, zona_horaria: v }))}>
                <SelectTrigger data-testid="form-zona-horaria" className="h-11 rounded-none border-[#0A0A0A]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value} data-testid={`tz-${tz.value}`}>{tz.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#A1A1AA]">Determina el día local para cortes, métricas, pernoctas, consumo de folios y las horas mostradas/impresas. Los datos se guardan en UTC.</p>
            </div>

            {/* Sprint 15.3 — Boleto perdido y leyenda impresa (ticket térmico) */}
            <div className="border border-[#e4e4e7] p-4 space-y-3" data-testid="boleto-perdido-config-block">
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B] block">Boleto perdido y leyenda impresa</Label>
              <div className="space-y-2">
                <Label className="text-[11px] uppercase font-bold text-[#71717A]">Costo por boleto perdido (MXN)</Label>
                <Input type="number" min="0" step="0.01" value={form.costo_boleto_perdido}
                  onChange={(e) => setForm((f) => ({ ...f, costo_boleto_perdido: e.target.value }))}
                  data-testid="form-costo-boleto-perdido" className="h-10 rounded-none border-[#0A0A0A] w-48" />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] uppercase font-bold text-[#71717A]">Leyenda impresa (ticket térmico) · máx 300</Label>
                <textarea value={form.leyenda_ticket_corta} maxLength={300}
                  onChange={(e) => setForm((f) => ({ ...f, leyenda_ticket_corta: e.target.value }))}
                  data-testid="leyenda-ticket-textarea" rows={3} placeholder="Texto corto que se imprime en el papel térmico…"
                  className="w-full border border-[#e4e4e7] p-3 text-sm text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#0A0A0A] resize-y" />
                <p className={`text-[10px] text-right ${(form.leyenda_ticket_corta || "").length >= 300 ? "text-[#EF4444] font-bold" : "text-[#A1A1AA]"}`} data-testid="leyenda-char-count">{(form.leyenda_ticket_corta || "").length}/300</p>
              </div>
            </div>

            {/* Sprint 15.2 — Contrato de adhesión (PROFECO/legal) */}
            <div className="border border-[#e4e4e7] p-4 space-y-2" data-testid="contrato-config-block">
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B] block">Contrato de Adhesión de Servicios (PROFECO/Legal)</Label>
              <p className="text-[11px] text-[#71717A]">Se muestra en la landing pública del cliente (/t/folio) dentro del acordeón legal.</p>
              <textarea value={form.contrato_adhesion} onChange={(e) => setForm((f) => ({ ...f, contrato_adhesion: e.target.value }))}
                data-testid="contrato-textarea" rows={5} placeholder="Pega aquí el texto completo del contrato de adhesión…"
                className="w-full border border-[#e4e4e7] p-3 text-sm text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#0A0A0A] resize-y" />
            </div>

            {/* Modules */}
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B] mb-3 block">Módulos habilitados</Label>
              <div className="space-y-px bg-[#e4e4e7] border border-[#e4e4e7]">
                {MODULES.map((m) => (
                  <div key={m.key} className="flex items-center justify-between bg-white px-4 py-3">
                    <span className="text-sm font-medium">{m.label}</span>
                    <Switch checked={form[m.key]} onCheckedChange={(v) => setForm({ ...form, [m.key]: v })}
                      data-testid={`toggle-${m.key}`} className="data-[state=checked]:bg-[#10B981]" />
                  </div>
                ))}
              </div>
            </div>

            {/* Valet Parking (Sprint 8.0) */}
            <div className="border border-[#e4e4e7] p-4 space-y-4" data-testid="valet-config-block">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold">Valet Parking · candado de caja</span>
                <Switch checked={form.valet_activo} onCheckedChange={(v) => setForm({ ...form, valet_activo: v })}
                  data-testid="toggle-valet-activo" className="data-[state=checked]:bg-[#10B981]" />
              </div>
              {form.valet_activo && (
                <div className="space-y-2 pt-1">
                  <Label className="text-xs tracking-[0.1em] uppercase font-bold text-[#52525B]">Tarifa de Valet (MXN)</Label>
                  <CurrencyField value={form.tarifa_valet === "" ? null : form.tarifa_valet}
                    onChange={(v) => setForm({ ...form, tarifa_valet: v == null ? "" : v })}
                    data-testid="form-tarifa-valet" placeholder="$0.00"
                    className="h-11 rounded-none border-[#0A0A0A]" />
                  <p className="text-[11px] text-[#71717A]">Si la tarifa es mayor a $0, la caseta exigirá una caja abierta para recibir vehículos de Valet.</p>
                  <div className="pt-3 mt-1 border-t border-[#e4e4e7] space-y-2">
                    <Label className="text-xs tracking-[0.1em] uppercase font-bold text-[#52525B]">Minutos de Tolerancia Valet</Label>
                    <Input type="number" min="0" value={form.minutos_tolerancia_valet}
                      onChange={(e) => setForm({ ...form, minutos_tolerancia_valet: e.target.value })}
                      data-testid="form-minutos-tolerancia-valet" placeholder="0"
                      className="h-11 rounded-none border-[#0A0A0A]" />
                    <p className="text-[11px] text-[#71717A]">Si el vehículo de Valet sale antes de estos minutos, el <b>servicio de Valet se cobra $0.00</b> automáticamente. <b>No afecta</b> el cálculo por Hora o Fracción del estacionamiento general. (0 = sin tolerancia).</p>
                  </div>
                  {/* Factor de Riesgo Valet */}
                  <div className="pt-3 mt-1 border-t border-[#e4e4e7] space-y-3" data-testid="factor-riesgo-block">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold">Habilitar Factor de Riesgo</span>
                      <Switch checked={form.habilitar_factor_riesgo} onCheckedChange={(v) => setForm({ ...form, habilitar_factor_riesgo: v })}
                        data-testid="toggle-factor-riesgo" className="data-[state=checked]:bg-[#10B981]" />
                    </div>
                    {form.habilitar_factor_riesgo && (
                      <div className="space-y-2">
                        <Label className="text-xs tracking-[0.1em] uppercase font-bold text-[#52525B]">Cuota por Ticket ($ MXN)</Label>
                        <CurrencyField value={form.cuota_factor_riesgo === "" ? null : form.cuota_factor_riesgo}
                          onChange={(v) => setForm({ ...form, cuota_factor_riesgo: v == null ? "" : v })}
                          data-testid="form-cuota-factor-riesgo" placeholder="$0.00"
                          className="h-11 rounded-none border-[#0A0A0A]" />
                        <p className="text-[11px] text-[#71717A]">Cuota que el valet aporta de su bolsa/propinas por cada boleto emitido (menos cancelaciones y cortesías). Se suma al efectivo a entregar al cerrar el turno.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Propinas (Sprint 17.0) — solo cobro con Tarjeta / Mercado Pago Point */}
            <div className="border border-[#e4e4e7] p-4 space-y-2" data-testid="propinas-config-block">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold">Habilitar solicitud de propinas</span>
                <Switch checked={form.propinas_activas} onCheckedChange={(v) => setForm({ ...form, propinas_activas: v })}
                  data-testid="toggle-propinas-activas" className="data-[state=checked]:bg-[#10B981]" />
              </div>
              <p className="text-[11px] text-[#71717A]">Al activarse, la caseta y el valet mostrarán opciones de propina ($10 / $20 / $50 / Personalizado) al cobrar con tarjeta. La propina es <b>No Facturable</b> y el cargo por servicio bancario se recalcula sobre subtotal + propina.</p>

              {form.propinas_activas && (
                <div className="pt-3 mt-1 border-t border-[#e4e4e7] space-y-2" data-testid="tope-propina-block">
                  <Label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Tope máximo de propina</Label>
                  <Select value={String(Math.round(Number(form.tope_propina_pct)) || 100)} onValueChange={(v) => setForm({ ...form, tope_propina_pct: Number(v) })}>
                    <SelectTrigger className="h-11 rounded-none border-[#0A0A0A]" data-testid="select-tope-propina">
                      <SelectValue placeholder="Selecciona el tope" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25" data-testid="tope-opt-25">25% del subtotal</SelectItem>
                      <SelectItem value="50" data-testid="tope-opt-50">50% del subtotal</SelectItem>
                      <SelectItem value="75" data-testid="tope-opt-75">75% del subtotal</SelectItem>
                      <SelectItem value="100" data-testid="tope-opt-100">100% del subtotal (recomendado)</SelectItem>
                      <SelectItem value="150" data-testid="tope-opt-150">150% del subtotal</SelectItem>
                      <SelectItem value="200" data-testid="tope-opt-200">200% del subtotal</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-[#71717A]">Límite de seguridad: la propina (fija o personalizada) no podrá superar este porcentaje del subtotal en la caseta ni en el valet.</p>
                </div>
              )}
            </div>

            {/* Cortesías (Sprint 31.0) — gate del botón "Aplicar Cortesía" en Caseta y Valet */}
            <div className="border border-[#e4e4e7] p-4 space-y-2" data-testid="cortesias-config-block">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold">Habilitar Cortesías</span>
                <Switch checked={form.cortesias_activas} onCheckedChange={(v) => setForm({ ...form, cortesias_activas: v })}
                  data-testid="toggle-cortesias-activas" className="data-[state=checked]:bg-[#A78BFA]" />
              </div>
              <p className="text-[11px] text-[#71717A]">Al activarse, los operadores con permiso podrán aplicar cortesías ($0.00) desde los modales de cobro de la Terminal y del Valet. Si se apaga, el botón <b>Aplicar Cortesía</b> se oculta para esta sucursal.</p>
            </div>

            {/* Asistentes de captura — consumo variable de folios (Bolsa Global del Tenant) */}
            <div className="border border-[#e4e4e7] p-4 space-y-3" data-testid="asistentes-captura-block">
              <Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Asistentes de captura (consumo de folios)</Label>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold">Habilitar Asistente de Voz <span className="text-[#71717A] font-normal">— 3 folios</span></span>
                <Switch checked={!!form.voz_ia_activa} onCheckedChange={(v) => setForm({ ...form, voz_ia_activa: v })}
                  data-testid="toggle-voz-ia" className="data-[state=checked]:bg-[#10B981]" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold">Habilitar Fotografía IA <span className="text-[#71717A] font-normal">— 12 folios</span></span>
                <Switch checked={!!form.foto_ia_activa} onCheckedChange={(v) => setForm({ ...form, foto_ia_activa: v })}
                  data-testid="toggle-foto-ia" className="data-[state=checked]:bg-[#10B981]" />
              </div>
              <p className="text-[11px] text-[#71717A]">Controlan qué asistentes aparecen en Terminal y Valet. La captura <b>manual siempre está disponible (1 folio)</b>. El consumo se descuenta de la Bolsa Global de Folios del Tenant.</p>
            </div>

            {/* Sprint 18.0 — permisos financieros (corte y gastos) */}
            <div className="border border-[#e4e4e7] p-4 space-y-3" data-testid="permisos-financieros-block">
              <Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Controles financieros (permisos)</Label>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold">Exigir permiso para el Corte de Caja</span>
                <Switch checked={!!form.requiere_permiso_corte} onCheckedChange={(v) => setForm({ ...form, requiere_permiso_corte: v })}
                  data-testid="toggle-requiere-permiso-corte" className="data-[state=checked]:bg-[#10B981]" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold">Exigir permiso para registrar Gastos</span>
                <Switch checked={!!form.requiere_permiso_gastos} onCheckedChange={(v) => setForm({ ...form, requiere_permiso_gastos: v })}
                  data-testid="toggle-requiere-permiso-gastos" className="data-[state=checked]:bg-[#10B981]" />
              </div>
              <p className="text-[11px] text-[#71717A]">Al activarse, solo los colaboradores con el permiso correspondiente podrán realizar el corte o registrar gastos en esta sucursal.</p>
            </div>

            {/* Sprint Pernoctas — Pensión Nocturna y Tolerancia */}
            <div className="border border-[#e4e4e7] p-4 space-y-3" data-testid="pernocta-config-block">
              <div className="flex items-center gap-2">
                <Moon className="h-4 w-4 text-[#6366F1]" />
                <Label className="text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Pensión Nocturna y Tolerancia</Label>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold">Habilitar Cobro de Pensión Nocturna (Valet · tarifa fija)</span>
                <Switch checked={!!form.tarifa_pernocta_activa} onCheckedChange={(v) => setForm({ ...form, tarifa_pernocta_activa: v })}
                  data-testid="toggle-tarifa-pernocta" className="data-[state=checked]:bg-[#6366F1]" />
              </div>
              {form.tarifa_pernocta_activa && (
                <div className="pt-3 mt-1 border-t border-[#e4e4e7] grid grid-cols-2 gap-3" data-testid="pernocta-fields">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Monto Fijo de Pernocta ($ MXN)</Label>
                    <Input type="number" min="0" step="0.01" value={form.monto_pernocta}
                      onChange={(e) => setForm({ ...form, monto_pernocta: e.target.value })}
                      data-testid="input-monto-pernocta" className="mt-1 h-11 rounded-none border-[#0A0A0A]" placeholder="150.00" />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Minutos de Tolerancia Pos-Cierre</Label>
                    <Input type="number" min="0" step="1" value={form.minutos_tolerancia_pos_cierre}
                      onChange={(e) => setForm({ ...form, minutos_tolerancia_pos_cierre: e.target.value })}
                      data-testid="input-minutos-tolerancia" className="mt-1 h-11 rounded-none border-[#0A0A0A]" placeholder="120" />
                  </div>
                </div>
              )}
              <p className="text-[11px] text-[#71717A]">Aplica solo a servicios de Valet: si el vehículo sale al día siguiente y supera la hora de cierre programada + la tolerancia, se cobra la tarifa fija de pernocta. El estacionamiento público por hora/fracción sigue cobrando el tiempo acumulado (sin pernocta fija).</p>
            </div>

            <button onClick={save} disabled={saving} data-testid="form-save-btn"
              className="btn-hard w-full h-12 bg-[#FFCC00] text-[#0A0A0A] border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Guardar cambios" : "Crear sucursal"}
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-none border-[#0A0A0A]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-black">¿Desactivar sucursal?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.nombre}" se desactivará (soft delete). Los datos se conservan y no se eliminan permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="delete-confirm" className="rounded-none bg-[#EF4444] hover:bg-[#dc2626]">Desactivar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* QR de acceso operativo */}
      <Dialog open={!!qrTarget} onOpenChange={(o) => !o && setQrTarget(null)}>
        <DialogContent className="rounded-none border-[#0A0A0A] bg-white">
          <DialogHeader>
            <DialogTitle className="font-display font-black">QR de Acceso · {qrTarget?.nombre}</DialogTitle>
            <DialogDescription>
              Escanea este código para abrir el login operativo con la empresa y la sucursal precargadas.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-5 pt-2">
            <div className="p-4 bg-white border border-[#0A0A0A]">
              {qrUrl && <QRCodeCanvas value={qrUrl} size={200} level="M" data-testid="qr-canvas" />}
            </div>
            <div className="w-full">
              <p className="text-[10px] uppercase tracking-wider font-bold text-[#52525B] mb-1.5">Enlace de acceso</p>
              <div className="flex gap-2">
                <input readOnly value={qrUrl} data-testid="qr-url"
                  className="flex-1 h-10 px-3 border border-[#e4e4e7] bg-gray-50 text-xs font-mono truncate" />
                <button onClick={async () => { try { await navigator.clipboard?.writeText(qrUrl); toast.success("Enlace copiado"); } catch { toast.error("No se pudo copiar el enlace"); } }}
                  data-testid="qr-copy" className="btn-hard h-10 px-3 bg-[#FFCC00] border border-[#0A0A0A] text-[#0A0A0A] flex items-center">
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            </div>
            <button onClick={() => {
                const canvas = document.querySelector('[data-testid="qr-canvas"]');
                if (!canvas) return;
                const a = document.createElement("a");
                a.href = canvas.toDataURL("image/png");
                a.download = `qr-acceso-${qrTarget?.nombre?.replace(/\s+/g, "-")}.png`;
                a.click();
                toast.success("QR descargado");
              }} data-testid="qr-download"
              className="btn-hard w-full h-11 bg-[#0A0A0A] text-[#FFCC00] border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2">
              <Download className="h-4 w-4" /> Descargar PNG
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
